import { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Flag, CaretRight } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

/**
 * Persistent floating "Resume round" pill — shown on every signed-in screen
 * whenever the player has an in-progress round so they can jump back in even
 * if they navigated several screens away. Hidden on the round screen itself
 * and on the round-summary screen.
 *
 * Refreshes on:
 *   - mount
 *   - window focus (e.g. user reopens the PWA / switches tab back)
 *   - route change (so finishing a round on the summary screen clears it)
 */
export default function ActiveRoundPill() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [active, setActive] = useState(null);
  // Per-session dismiss — re-shows on next session / refocus.
  const [dismissed, setDismissed] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/rounds");
      const list = Array.isArray(data) ? data : data?.items || [];
      const inProgress = list.find((r) => !r.completed) || null;
      setActive(inProgress);
    } catch {
      setActive(null);
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [user, refresh, pathname]);

  if (!user) return null;
  if (!active) return null;
  if (dismissed) return null;
  // Hide on the round screen + summary itself — pill would be noise there.
  if (pathname.startsWith(`/app/round/${active.id}`)) return null;
  if (pathname.startsWith(`/app/round-summary/${active.id}`)) return null;

  const lastScored = (active.scores || [])
    .slice()
    .sort((a, b) => b.hole_number - a.hole_number)[0];
  const nextHole = lastScored ? Math.min(18, lastScored.hole_number + 1) : 1;

  return (
    <div
      data-testid="active-round-pill"
      className="fixed inset-x-0 bottom-40 sm:bottom-32 z-30 px-4 pointer-events-none flex justify-center"
    >
      <div
        className="pointer-events-auto inline-flex items-stretch rounded-full shadow-xl overflow-hidden"
        style={{
          background: "var(--accent-terracotta)",
          color: "#F2EEE7",
          border: "1.5px solid rgba(255,255,255,0.18)",
        }}
      >
        <Link
          to={`/app/round/${active.id}`}
          data-testid="active-round-pill-resume"
          className="flex items-center gap-2 pl-4 pr-3 py-2.5 hover:opacity-95 transition-opacity"
        >
          <Flag size={16} weight="fill" />
          <div className="text-left leading-tight">
            <div className="text-[9px] uppercase tracking-[2.5px] font-bold opacity-80">
              Round in progress
            </div>
            <div className="font-heading font-bold text-sm truncate max-w-[10rem]">
              {active.course_name} · Hole {nextHole}
            </div>
          </div>
          <CaretRight size={14} weight="bold" />
        </Link>
        <button
          type="button"
          aria-label="Hide for now"
          data-testid="active-round-pill-dismiss"
          onClick={() => setDismissed(true)}
          className="px-3 border-l border-white/15 text-[11px] uppercase tracking-wider font-bold opacity-70 hover:opacity-100 transition-opacity"
        >
          Hide
        </button>
      </div>
    </div>
  );
}
